import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { API_ENDPOINTS } from "./api/config";
import "./sign_in.css";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const token = searchParams.get("token") || "";
  const email = searchParams.get("email") || "";

  const [verifyingToken, setVerifyingToken] = useState(true);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  // Phase 1: Pre-validate parameters immediately upon landing
  useEffect(() => {
    const preValidateToken = async () => {
      if (!token || !email) {
        setError("Invalid or broken password reset link.");
        setVerifyingToken(false);
        return;
      }

      try {
        const response = await fetch(
          `${API_ENDPOINTS.VALIDATE_TOKEN}?token=${encodeURIComponent(token)}&email=${encodeURIComponent(email)}`
        );
        if (!response.ok) {
          const data = await response.json();
          setError(data.message || "This reset link is invalid or has expired.");
        }
      } catch (err) {
        console.error("Token pre-validation failure:", err);
        setError("Unable to connect to the security server.");
      } finally {
        setVerifyingToken(false);
      }
    };

    preValidateToken();
  }, [token, email]);

  // Phase 2: Ultimate Form Submit Authority
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!token || !email) {
      setError("Invalid or expired password reset link configuration.");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters long.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(API_ENDPOINTS.RESET_PASSWORD, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, token, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Something went wrong. Please try again.");
      }

      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="si-root">
      <div className="si-left">
        <Link to="/" className="si-logo">Rembo<span>List</span></Link>

        <div className="si-form-area">
          <div className="si-eyebrow">Account Recovery</div>
          <h1 className="si-title">Create new<br /><em>password</em></h1>

          {/* Conditional state switches mapped beautifully inside the form block container */}
          {verifyingToken ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", padding: "1.5rem 0", color: "#aaa" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                <span className="si-spinner" style={{ width: "18px", height: "18px", borderWidth: "2px" }} />
                <span style={{ fontSize: "0.85rem", fontStyle: "italic" }}>Verifying security credentials...</span>
              </div>
            </div>
          ) : !submitted ? (
            <>
              <p className="si-subtitle">
                Set a secure password for your profile linked to <strong>{email || "your account"}</strong>.
              </p>

              {error && <div className="si-error"><span>⚠</span>{error}</div>}

              {/* Only reveal inputs if initial verification passed successfully without errors */}
              {!error && (
                <form onSubmit={handleSubmit}>
                  <div className="si-field">
                    <label className="si-label">New Password</label>
                    <input
                      className="si-input"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={loading}
                    />
                  </div>

                  <div className="si-field">
                    <label className="si-label">Confirm New Password</label>
                    <input
                      className="si-input"
                      type="password"
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      disabled={loading}
                    />
                  </div>

                  <button className="si-btn" type="submit" disabled={loading}>
                    {loading && <span className="si-spinner" />}
                    {loading ? "Updating password…" : "Reset Password"}
                  </button>
                </form>
              )}
            </>
          ) : (
            <div className="si-success-msg" style={{ marginTop: "1rem" }}>
              <p style={{ fontWeight: 500, marginBottom: "0.5rem" }}>Password Updated Successfully!</p>
              <p style={{ opacity: 0.8, lineHeight: 1.5 }}>
                Your account credentials have been securely rewritten. You can now access your dashboard workspace.
              </p>
              <button 
                className="si-panel-btn" 
                style={{ marginTop: "1.5rem", width: "100%", color: "#1a1a1a", borderColor: "#1a1a1a" }}
                onClick={() => navigate("/signin")}
              >
                Go to Sign In
              </button>
            </div>
          )}

          <div className="si-divider"><span>or</span></div>
          <div className="si-switch">
            Abort recovery sequence?{" "}
            <a href="#" onClick={(e) => { e.preventDefault(); navigate("/signin"); }}>
              Cancel
            </a>
          </div>
        </div>

        <p className="si-footer-note">
          Secure lifecycle token architecture certified by RemboList Security Protocol v3.0
        </p>
      </div>

      <div className="si-right">
        <div className="si-right-inner">
          <div className="si-panel-tag">Security First</div>
          <h2 className="si-panel-title">Protecting your<br /><em>digital craft</em></h2>
          <p className="si-panel-text">
            We use industry-standard encryption to ensure your account details 
            remain private and your connections secure.
          </p>
        </div>
      </div>
    </div>
  );
}